import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { knowledgeBaseService } from '@/services/knowledge-base'
import {
  SYNC_FREQUENCY_OPTIONS,
  CRAWL_DEPTH_OPTIONS,
  formatSyncDate,
  getSyncStatusLabel,
  getSyncStatusColor,
  type SyncFrequency,
} from '@/schemas/url-sync'
import {
  Globe,
  RefreshCw,
  Loader2,
  Clock,
  Layers,
  AlertCircle,
  Plus,
  X,
  FileText,
} from 'lucide-react'

interface UrlSyncConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  knowledgeBaseId: string
  knowledgeBaseName: string
  onSyncConfigured?: () => void
  onError?: (message: string) => void
}

export function UrlSyncConfigDialog({
  open,
  onOpenChange,
  projectId,
  knowledgeBaseId,
  knowledgeBaseName,
  onSyncConfigured,
  onError,
}: UrlSyncConfigDialogProps) {
  const { t } = useTranslation('knowledge')

  // State
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [internalError, setInternalError] = useState<string | null>(null)

  // Config state
  const [urls, setUrls] = useState<string[]>([])
  const [newUrl, setNewUrl] = useState('')
  const [frequency, setFrequency] = useState<SyncFrequency>('manual')
  const [crawlDepth, setCrawlDepth] = useState(0)
  const [extractCleanText, setExtractCleanText] = useState(true)
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<string>('idle')

  // Load existing config
  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true)
      const config = await knowledgeBaseService.getUrlSyncConfig(
        projectId,
        knowledgeBaseId
      )

      setUrls(config.urls)
      setFrequency((config.frequency as SyncFrequency) || 'manual')
      setCrawlDepth(config.crawl_depth)
      setExtractCleanText(config.extract_clean_text)
      setLastSyncAt(config.last_sync_at)
      setSyncStatus(config.sync_status)
    } catch (err) {
      console.error('Failed to load URL sync config:', err)
      // Not an error - config might not exist yet
    } finally {
      setIsLoading(false)
    }
  }, [projectId, knowledgeBaseId])

  useEffect(() => {
    if (open) {
      loadConfig()
    }
  }, [open, loadConfig])

  const handleAddUrl = () => {
    const trimmed = newUrl.trim()
    if (!trimmed) return

    // Protocol validation (SSRF protection on client)
    if (!trimmed.startsWith('https://') && !trimmed.startsWith('http://')) {
      setInternalError(t('sync.urlInvalidProtocol'))
      return
    }

    // Basic URL validation
    try {
      new URL(trimmed)
    } catch {
      setInternalError(t('sync.urlInvalid'))
      return
    }

    if (urls.includes(trimmed)) {
      setInternalError(t('sync.urlDuplicate'))
      return
    }

    setUrls([...urls, trimmed])
    setNewUrl('')
    setInternalError(null)
  }

  const handleRemoveUrl = (index: number) => {
    setUrls(urls.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddUrl()
    }
  }

  const handleSave = async () => {
    setInternalError(null)

    if (urls.length === 0) {
      setInternalError(t('sync.urlAtLeastOne'))
      return
    }

    try {
      setIsSaving(true)
      await knowledgeBaseService.configureUrlSync(projectId, knowledgeBaseId, {
        enabled: true,
        urls,
        frequency,
        crawl_depth: crawlDepth,
        extract_clean_text: extractCleanText,
      })

      onSyncConfigured?.()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save URL sync config:', err)
      const message = t('sync.urlSaveError')
      setInternalError(message)
      onError?.(message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSyncNow = async () => {
    setInternalError(null)

    if (urls.length === 0) {
      setInternalError(t('sync.urlConfigureFirst'))
      return
    }

    try {
      setIsSyncing(true)

      // Save config first
      await knowledgeBaseService.configureUrlSync(projectId, knowledgeBaseId, {
        enabled: true,
        urls,
        frequency,
        crawl_depth: crawlDepth,
        extract_clean_text: extractCleanText,
      })

      // Trigger sync
      const result = await knowledgeBaseService.triggerUrlSync(
        projectId,
        knowledgeBaseId
      )

      setSyncStatus(result.status)
      onSyncConfigured?.()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to trigger URL sync:', err)
      const message = t('sync.urlSyncError')
      setInternalError(message)
      onError?.(message)
    } finally {
      setIsSyncing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-400" />
            {t('sync.urlConfigTitle')}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('sync.urlConfigDesc')}{' '}
            <span className="text-gray-200 font-medium">{knowledgeBaseName}</span>
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Error Alert */}
            {internalError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                <span className="text-sm text-red-400">{internalError}</span>
              </div>
            )}

            {/* URL Input */}
            <div className="space-y-2">
              <Label className="text-gray-200 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                URLs
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('sync.urlPlaceholder')}
                  className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddUrl}
                  disabled={!newUrl.trim()}
                  className="border-gray-600 hover:bg-gray-800 flex-shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* URL List */}
              {urls.length > 0 ? (
                <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto">
                  {urls.map((url) => (
                    <div
                      key={url}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-800 border border-gray-700"
                    >
                      <Globe className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />
                      <span className="text-sm text-gray-300 truncate flex-1">{url}</span>
                      <button
                        onClick={() => handleRemoveUrl(urls.indexOf(url))}
                        className="text-gray-500 hover:text-red-400 transition-colors flex-shrink-0"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {t('sync.urlNoneAdded')}
                </p>
              )}
            </div>

            {/* Crawl Depth */}
            <div className="space-y-2">
              <Label className="text-gray-200 flex items-center gap-2">
                <Layers className="h-4 w-4" />
                {t('sync.urlCrawlDepth')}
              </Label>
              <Select
                value={String(crawlDepth)}
                onValueChange={(v) => setCrawlDepth(Number(v))}
              >
                <SelectTrigger className="bg-gray-800 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {CRAWL_DEPTH_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      <span>{option.label}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        - {option.description}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Extract Clean Text */}
            <div className="flex items-center justify-between px-3 py-3 bg-gray-800/30 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-gray-400" />
                <div>
                  <Label className="text-gray-200">{t('sync.urlExtractClean')}</Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('sync.urlExtractCleanHelper')}
                  </p>
                </div>
              </div>
              <Checkbox
                checked={extractCleanText}
                onCheckedChange={(checked) => setExtractCleanText(checked === true)}
                className="h-5 w-5 border-gray-500"
              />
            </div>

            {/* Sync Frequency */}
            <div className="space-y-2">
              <Label className="text-gray-200 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {t('sync.urlRecrawlFrequency')}
              </Label>
              <Select value={frequency} onValueChange={(v) => setFrequency(v as SyncFrequency)}>
                <SelectTrigger className="bg-gray-800 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {SYNC_FREQUENCY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sync Status */}
            {lastSyncAt && (
              <div className="flex items-center justify-between px-3 py-2 bg-gray-800/30 rounded-md">
                <span className="text-sm text-gray-400">{t('sync.urlLastCrawl')}:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-300">
                    {formatSyncDate(lastSyncAt)}
                  </span>
                  <Badge variant={getSyncStatusColor(syncStatus)}>
                    {getSyncStatusLabel(syncStatus)}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-gray-400"
          >
            {t('sync.cancel')}
          </Button>
          <Button
            variant="outline"
            onClick={handleSyncNow}
            disabled={isSyncing || isSaving || urls.length === 0}
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {t('sync.urlCrawlNow')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isSyncing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            {t('sync.urlSaveConfig')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
